import { FareCalendarResponse } from '@/src/api/rtkQuery/calendarApi'

export interface TransformedFareCalendarData {
    fareCalendar: Record<string, { amount: number; fareCategory: string }>
    cheapestPricesByMonth: Record<string, number>
}

/**
 * Transforms fare calendar API response into lookup objects for efficient date-based access
 * Converts date format from "DD-MM-YYYY" to "YYYY-MM-DD" and calculates cheapest prices per month
 * 
 * @param response - Raw API response with dayData array
 * @returns Transformed data with fareCalendar lookup and cheapestPricesByMonth lookup
 */
export const transformFareCalendarData = (
    response: FareCalendarResponse | null | undefined
): TransformedFareCalendarData => {
    // Default empty objects if no response or invalid data
    const defaultResult: TransformedFareCalendarData = {
        fareCalendar: {},
        cheapestPricesByMonth: {}
    }

    if (!response?.dayData || !Array.isArray(response.dayData)) {
        return defaultResult
    }

    const lookup: Record<string, { amount: number; fareCategory: string }> = {}
    const cheapestByMonth: Record<string, number> = {}

    try {
        response.dayData.forEach((item) => {
            // Validate date format before parsing
            if (!item.date || typeof item.date !== 'string') {
                console.warn('Invalid date format in fare calendar response:', item)
                return
            }

            // Convert "DD-MM-YYYY" to "YYYY-MM-DD" for lookup key
            const dateParts = item.date.split('-')
            if (dateParts.length !== 3) {
                console.warn('Invalid date format in fare calendar response:', item.date)
                return
            }

            const [day, month, year] = dateParts
            const dateKey = `${year}-${month}-${day}`

            // Only process items with valid fare data
            if (item.farePresent && item.amount && typeof item.amount === 'number' && item.amount > 0) {
                lookup[dateKey] = {
                    amount: item.amount,
                    fareCategory: item.fareCategory
                }

                // Calculate cheapest price per month (YYYY-MM format)
                const monthKey = `${year}-${month}`
                if (!cheapestByMonth[monthKey] || item.amount < cheapestByMonth[monthKey]) {
                    cheapestByMonth[monthKey] = item.amount
                }
            }
        })

        return {
            fareCalendar: lookup,
            cheapestPricesByMonth: cheapestByMonth
        }
    } catch (error) {
        // Handle any unexpected errors during data transformation
        console.error('Error processing fare calendar data:', error)
        return defaultResult
    }
}

